/**
 * Gestion des listes sociales (amis, ignorés) et options conditionnelles.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { BACKEND_URL } from '../config';
import type { SocialListEntry, SocialProfileData, PlayerCountMode, CompetRolePreference } from '../types/draft';
import { DEFAULT_ACCEPTED_PLAYER_COUNTS, COMPET_ROLE_PREFERENCES, COMPET_ROLE_LABELS } from '../types/draft';

interface SocialListsSettingsProps {
  pseudo: string;
  token?: string;
  compet: boolean;
}

interface SearchResult {
  pseudo: string;
  pseudoDisplay: string;
  photoUrl?: string | null;
}

function SocialListSection({
  title,
  description,
  entries,
  emptyLabel,
  addPlaceholder,
  onRemove,
  onAdd,
  searching,
  searchResults,
  onSearch,
  addLabel,
}: {
  title: string;
  description: string;
  entries: SocialListEntry[];
  emptyLabel: string;
  addPlaceholder: string;
  onRemove: (pseudo: string) => void;
  onAdd: (pseudo: string) => void;
  searching: boolean;
  searchResults: SearchResult[];
  onSearch: (query: string) => void;
  addLabel: string;
}) {
  const [query, setQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (value: string) => {
    setQuery(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onSearch(value), 250);
  };

  return (
    <div className="social-list-section">
      <div className="social-list-header">
        <div>
          <div className="social-list-title">{title}</div>
          <div className="social-list-desc">{description}</div>
        </div>
        <button
          type="button"
          className="social-list-add-btn"
          onClick={() => setShowSearch(v => !v)}
        >
          {showSearch ? 'Fermer' : addLabel}
        </button>
      </div>

      {showSearch && (
        <div className="social-list-search">
          <input
            type="text"
            value={query}
            placeholder={addPlaceholder}
            onChange={e => handleChange(e.target.value)}
          />
          {searching && <div className="social-list-search-hint">Recherche…</div>}
          {!searching && searchResults.length > 0 && (
            <div className="social-list-search-results">
              {searchResults.map(player => (
                <button
                  key={player.pseudo}
                  type="button"
                  className="social-list-search-row"
                  onClick={() => {
                    onAdd(player.pseudo);
                    setQuery('');
                    setShowSearch(false);
                  }}
                >
                  <PlayerAvatar pseudo={player.pseudo} size={24} photoUrl={player.photoUrl} />
                  <span>{player.pseudoDisplay}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="social-list-empty">{emptyLabel}</div>
      ) : (
        <ul className="social-list-entries">
          {entries.map(entry => (
            <li key={entry.pseudo} className="social-list-entry">
              <PlayerAvatar pseudo={entry.pseudo} size={28} />
              <span className="social-list-entry-name">{entry.pseudoDisplay}</span>
              <button
                type="button"
                className="social-list-remove-btn"
                onClick={() => onRemove(entry.pseudo)}
                title={`Retirer ${entry.pseudoDisplay}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SocialListsSettings({ pseudo, token, compet }: SocialListsSettingsProps) {
  const [data, setData] = useState<SocialProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [friendQueryResults, setFriendQueryResults] = useState<SearchResult[]>([]);
  const [ignoreQueryResults, setIgnoreQueryResults] = useState<SearchResult[]>([]);
  const [searchingFriends, setSearchingFriends] = useState(false);
  const [searchingIgnored, setSearchingIgnored] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/profile/social?pseudo=${encodeURIComponent(pseudo)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error('[SocialListsSettings] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [pseudo, token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (partial: Record<string, unknown>) => {
    if (!token || !data) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/social`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pseudo, ...partial }),
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error('[SocialListsSettings] Save error:', err);
    } finally {
      setSaving(false);
    }
  }, [pseudo, token, data]);

  const searchPlayers = useCallback(async (query: string, list: 'friends' | 'ignored') => {
    if (query.trim().length < 2) {
      if (list === 'friends') setFriendQueryResults([]);
      else setIgnoreQueryResults([]);
      return;
    }

    const setSearching = list === 'friends' ? setSearchingFriends : setSearchingIgnored;
    const setResults = list === 'friends' ? setFriendQueryResults : setIgnoreQueryResults;

    setSearching(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(query)}&limit=8&compet=${compet}`,
      );
      const json = await res.json();
      const existing = new Set([
        ...(data?.friends ?? []).map(e => e.pseudo.toLowerCase()),
        ...(data?.ignored ?? []).map(e => e.pseudo.toLowerCase()),
        pseudo.toLowerCase(),
      ]);
      const players = (json.players ?? []).filter(
        (p: SearchResult) => !existing.has(p.pseudo.toLowerCase()),
      );
      setResults(players);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [compet, data, pseudo]);

  const addToList = (listKey: 'friends' | 'ignored', targetPseudo: string) => {
    if (!data) return;
    const current = data[listKey].map(e => e.pseudo);
    if (current.some(p => p.toLowerCase() === targetPseudo.toLowerCase())) return;
    save({ [listKey]: [...current, targetPseudo] });
  };

  const removeFromList = (listKey: 'friends' | 'ignored', targetPseudo: string) => {
    if (!data) return;
    save({
      [listKey]: data[listKey]
        .filter(e => e.pseudo.toLowerCase() !== targetPseudo.toLowerCase())
        .map(e => e.pseudo),
    });
  };

  const togglePlayerCount = (count: PlayerCountMode) => {
    if (!data) return;
    const current = data.acceptedPlayerCounts ?? [...DEFAULT_ACCEPTED_PLAYER_COUNTS];
    const has = current.includes(count);
    let next: PlayerCountMode[];

    if (has) {
      if (current.length <= 1) return;
      next = current.filter(n => n !== count);
    } else {
      next = [...current, count].sort((a, b) => a - b) as PlayerCountMode[];
    }

    save({ acceptedPlayerCounts: next });
  };

  const setCompetRolePreference = (pref: CompetRolePreference) => {
    if (!data || data.competRolePreference === pref) return;
    save({ competRolePreference: pref });
  };

  if (loading) {
    return <div className="availability-loading">Chargement…</div>;
  }

  if (!token || !data) {
    return <div className="availability-loading">Session requise.</div>;
  }

  return (
    <div className={`social-lists-settings ${saving ? 'saving' : ''}`}>
      <div className="social-list-toggles">
        <label className="settings-toggle-row">
          <div className="settings-toggle-info">
            <span className="settings-toggle-label">Accepter les amis même si je suis bougon ou timide</span>
            <span className="settings-toggle-description">
              Les joueurs de votre liste d&apos;amis pourront toujours vous inviter (comportement « détendu »).
            </span>
          </div>
          <div className="settings-toggle-switch-container">
            <input
              type="checkbox"
              className="settings-toggle-input"
              checked={data.acceptIfFriend}
              onChange={e => save({ acceptIfFriend: e.target.checked })}
            />
            <span className="settings-toggle-switch" />
          </div>
        </label>

        <label className="settings-toggle-row">
          <div className="settings-toggle-info">
            <span className="settings-toggle-label">Accepter les coéquipiers même si je suis bougon ou timide</span>
            <span className="settings-toggle-description">
              Les membres de votre équipe bénéficient de la même exception (liste automatique ci-dessous).
            </span>
          </div>
          <div className="settings-toggle-switch-container">
            <input
              type="checkbox"
              className="settings-toggle-input"
              checked={data.acceptIfTeammate}
              onChange={e => save({ acceptIfTeammate: e.target.checked })}
            />
            <span className="settings-toggle-switch" />
          </div>
        </label>
      </div>

      <div className="social-list-section social-compet-role">
        <div className="social-list-title">Exigence de rôle (compétition)</div>
        <div className="social-list-desc">
          Indiquez si vous souhaitez jouer uniquement en attaque ou en défense.
          Visible par les autres joueurs en espace compétition lors des invitations.
        </div>
        <div className="player-format-toggles">
          {COMPET_ROLE_PREFERENCES.map(pref => {
            const active = (data.competRolePreference ?? 'none') === pref;
            const info = COMPET_ROLE_LABELS[pref];
            return (
              <button
                key={pref}
                type="button"
                className={`player-format-toggle ${active ? 'active' : ''}`}
                onClick={() => setCompetRolePreference(pref)}
                title={info.description}
              >
                {info.label}
              </button>
            );
          })}
        </div>
      </div>

      {!compet && (
        <div className="social-list-section social-player-formats">
          <div className="social-list-title">Formats de jeu acceptés (détente)</div>
          <div className="social-list-desc">
            Les invitations à une table dont le format n&apos;est pas coché seront refusées
            silencieusement. En compétition, toutes les parties sont à 4 joueurs — ce réglage
            ne s&apos;applique pas.
          </div>
          <div className="player-format-toggles">
            {DEFAULT_ACCEPTED_PLAYER_COUNTS.map(count => {
              const active = (data.acceptedPlayerCounts ?? DEFAULT_ACCEPTED_PLAYER_COUNTS).includes(count);
              return (
                <button
                  key={count}
                  type="button"
                  className={`player-format-toggle ${active ? 'active' : ''}`}
                  onClick={() => togglePlayerCount(count)}
                  title={active ? `Refuser les invitations à ${count} joueurs` : `Accepter les invitations à ${count} joueurs`}
                >
                  {count} joueurs
                </button>
              );
            })}
          </div>
          <div className="player-format-hint">
            Au moins un format doit rester sélectionné.
          </div>
        </div>
      )}

      {data.teammates.length > 0 && (
        <div className="social-list-section social-teammates-section">
          <div className="social-list-title">Coéquipiers (automatique)</div>
          <div className="social-list-desc">
            Membres de votre équipe — détectés automatiquement si vous êtes inscrit dans une équipe.
          </div>
          <ul className="social-list-entries">
            {data.teammates.map(entry => (
              <li key={entry.pseudo} className="social-list-entry readonly">
                <PlayerAvatar pseudo={entry.pseudo} size={28} />
                <div className="social-list-entry-info">
                  <span className="social-list-entry-name">{entry.pseudoDisplay}</span>
                  {entry.teamName && (
                    <span className="social-list-entry-team">{entry.teamName}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SocialListSection
        title="Liste d'amis"
        description="Joueurs que vous considérez comme amis (peuvent être favorisés pour les invitations)."
        entries={data.friends}
        emptyLabel="Aucun ami enregistré."
        addPlaceholder="Rechercher un joueur à ajouter…"
        addLabel="Ajouter un ami"
        onRemove={p => removeFromList('friends', p)}
        onAdd={p => addToList('friends', p)}
        searching={searchingFriends}
        searchResults={friendQueryResults}
        onSearch={q => searchPlayers(q, 'friends')}
      />

      <SocialListSection
        title="Joueurs ignorés"
        description="Leurs invitations seront refusées automatiquement et silencieusement."
        entries={data.ignored}
        emptyLabel="Aucun joueur ignoré."
        addPlaceholder="Rechercher un joueur à ignorer…"
        addLabel="Ajouter à ignorer"
        onRemove={p => removeFromList('ignored', p)}
        onAdd={p => addToList('ignored', p)}
        searching={searchingIgnored}
        searchResults={ignoreQueryResults}
        onSearch={q => searchPlayers(q, 'ignored')}
      />
    </div>
  );
}
